import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  FilesGroupedByRefId,
  ReferenceMinimal,
  TransformedFile,
  useStudyReferenceFilesReturn,
} from './useReferenceFilesTypes';
import { useFetchAndTransformFilesInApi } from './useFetchAndTransformFilesInApi';
import { Study } from 'pages/StudyList/StudyList';

export function useStudyReferenceFiles(
  category: string,
  studies: Study[],
  pageSize: number,
  reviewId?: string
): useStudyReferenceFilesReturn {
  const [refreshError, setRefreshError] = useState<boolean>(false);

  const hasReferenceFiles = useMemo(
    () =>
      category === 'select' ||
      category === 'extract' ||
      category === 'excluded' ||
      category === 'included',
    [category]
  );

  const referenceIds = useMemo(() => {
    if (!hasReferenceFiles) {
      return [];
    }

    return studies.flatMap((s) =>
      (s.references || []).map((r: ReferenceMinimal) => r.id)
    );
  }, [studies, hasReferenceFiles]);

  const refIds = useMemo(
    () => Array.from(new Set(referenceIds.filter(Boolean))),
    [referenceIds]
  );

  const { fetchFiles: fetchFilesInApi } = useFetchAndTransformFilesInApi(
    reviewId
  );

  const [filesGroupedById, setFilesGroupedById] = useState<FilesGroupedByRefId>(
    new Map()
  );

  // Track version per reference ID
  const [versionByRefId, setVersionByRefId] = useState<Map<number, number>>(
    new Map()
  );

  const refIdsString = refIds.join(',');

  const incrementVersionForRefs = useCallback((referenceIds: number[]) => {
    setVersionByRefId((prev) => {
      const updated = new Map(prev);
      referenceIds.forEach((refId) => {
        updated.set(refId, (prev.get(refId) || 0) + 1);
      });
      return updated;
    });
  }, []);

  const refreshRefs = useCallback(
    async (referenceIds: number[]): Promise<void> => {
      if (!referenceIds || referenceIds.length === 0) return;

      try {
        const fetchedFilesMap = await fetchFilesInApi(referenceIds);

        setFilesGroupedById((prevFiles) => {
          const updatedFiles = new Map(prevFiles);

          // Only touch the refreshed references: set those with files, drop
          // those that came back empty. Entries for other references are left
          // untouched so an empty refresh for one ref can't wipe the rest.
          referenceIds.forEach((refId) => {
            const files = fetchedFilesMap.get(refId);
            if (files && files.length > 0) {
              updatedFiles.set(refId, files);
            } else {
              updatedFiles.delete(refId);
            }
          });

          return updatedFiles;
        });

        incrementVersionForRefs(referenceIds);
        setRefreshError(false);
      } catch (error) {
        setRefreshError(true);
        throw error;
      }
    },
    [fetchFilesInApi, incrementVersionForRefs]
  );

  const refreshPage = useCallback(
    async (referenceId: number): Promise<void> => {
      // Find which study contains this referenceId (pagination is study-based, not reference-based)
      const studyIndex = studies.findIndex((study) =>
        (study.references || []).some(
          (r: ReferenceMinimal) => r.id === referenceId
        )
      );
      if (studyIndex === -1) return; // referenceId not found in any study

      const currentPage = Math.floor(studyIndex / pageSize) + 1;

      if (!hasReferenceFiles || !reviewId) return;

      // Calculate current page studies
      const startIdx = (currentPage - 1) * pageSize;
      const endIdx = startIdx + pageSize;
      const pageStudies = studies.slice(startIdx, endIdx);

      // Get all reference IDs from studies on the current page
      const currentPageRefIds = Array.from(
        new Set(
          pageStudies
            .flatMap((study) =>
              (study.references || []).map((r: ReferenceMinimal) => r.id)
            )
            .filter(Boolean)
        )
      );

      if (currentPageRefIds.length === 0) return;
      await refreshRefs(currentPageRefIds);
    },
    [studies, pageSize, hasReferenceFiles, reviewId, refreshRefs]
  );

  const refreshReference = useCallback(
    async (referenceId: number): Promise<void> => {
      await refreshRefs([referenceId]);
    },
    [referenceIds, pageSize, refreshRefs]
  );

  const getFilesForReference = useCallback(
    (referenceId: number): TransformedFile[] => {
      if (
        !hasReferenceFiles ||
        !filesGroupedById ||
        filesGroupedById.size === 0
      ) {
        return [];
      }

      return filesGroupedById.get(Number(referenceId)) ?? [];
    },
    [filesGroupedById]
  );

  const getRefreshVersionForReference = useCallback(
    (referenceId: number): number => {
      return versionByRefId.get(referenceId) || 0;
    },
    [versionByRefId]
  );

  useEffect(() => {
    if (!hasReferenceFiles) return;

    const lastRefId = refIds[refIds.length - 1];
    refreshPage(lastRefId);
  }, [reviewId, refIdsString, hasReferenceFiles]);

  return useMemo<useStudyReferenceFilesReturn>(
    () => ({
      refreshError,
      refreshPage,
      refreshReference,
      getFilesForReference,
      getRefreshVersionForReference,
    }),
    [
      filesGroupedById,
      versionByRefId,
      refreshError,
      refreshPage,
      refreshReference,
      getFilesForReference,
      getRefreshVersionForReference,
      incrementVersionForRefs,
      refIds,
    ]
  );
}
